/**
 * Sistema de invalidación de caché por versión.
 *
 * Cómo usar:
 *  1. Cada vez que quieras "resetear" el caché de los usuarios (por ejemplo,
 *     al subir un popup nuevo, cambiar features, limpiar localStorage viejo,
 *     etc.), incrementa APP_VERSION (ej: "2025.10.30-1" -> "2025.10.30-2").
 *  2. El próximo render llamará a runCacheInvalidation(), comparará la
 *     versión guardada con la actual, y si difiere limpiará:
 *       - localStorage (excepto las claves listadas en PRESERVE_KEYS)
 *       - sessionStorage
 *       - Cache Storage API (caches.*)
 *       - Service Workers registrados
 *  3. Guarda la nueva versión para que el reset solo corra una vez por
 *     usuario por release.
 */

// 👉 BUMP esto cuando quieras forzar un reset global de caché en clientes.
//    Formato sugerido: YYYY.MM.DD-N
export const APP_VERSION = "2026.05.08-3";

const VERSION_STORAGE_KEY = "__app_version__";

// Claves de localStorage que NO se deben borrar al invalidar caché.
// Agrega aquí cosas que quieras preservar entre versiones (ej: auth tokens,
// preferencias del usuario, favoritos persistidos, etc.).
const PRESERVE_KEYS = [
  // "auth_token",
  // "favorites",
];

const safe = (fn) => {
  try {
    return fn();
  } catch (err) {
    console.warn("[cacheVersion] operación falló:", err);
    return null;
  }
};

const clearLocalStoragePreserving = () => {
  safe(() => {
    const preserved = {};
    PRESERVE_KEYS.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v !== null) preserved[k] = v;
    });
    localStorage.clear();
    Object.entries(preserved).forEach(([k, v]) => localStorage.setItem(k, v));
  });
};

const clearCacheStorage = async () => {
  if (typeof caches === "undefined") return;
  await safe(async () => {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  });
};

const unregisterServiceWorkers = async () => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator))
    return;
  await safe(async () => {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  });
};

/**
 * Compara la versión almacenada con APP_VERSION y, si difiere, limpia caches.
 * Llamar lo antes posible en el bootstrap (antes de renderizar React).
 */
export const runCacheInvalidation = () => {
  const stored = safe(() => localStorage.getItem(VERSION_STORAGE_KEY));

  if (stored === APP_VERSION) return false;

  console.info(
    `[cacheVersion] versión cambió (${stored ?? "ninguna"} → ${APP_VERSION}), limpiando caché del cliente...`,
  );

  clearLocalStoragePreserving();
  safe(() => sessionStorage.clear());

  // Asíncronas (no bloqueamos el render por estas)
  clearCacheStorage();
  unregisterServiceWorkers();

  safe(() => localStorage.setItem(VERSION_STORAGE_KEY, APP_VERSION));
  return true;
};
